import React, { useEffect, useState } from "react";
import { ConfirmDialog, useNotifications, ValidationResult, TextField, FormValidationSummary } from "sprint-common-web";

import { useRequestCloudFileFix } from "./useRequestCloudFileFix";
import { useRequestCloudFixValidation } from "./useRequestCloudFixValidation";

interface RequestCloudFileFixDialogProps {
    deliveryFileName: string;
    filePath: string;
    cloudAssetId?: string;
    cloudFileId: string;
    isOpen: boolean;
    onClose(): void;
}

export const RequestCloudFileFixDialog: React.VFC<RequestCloudFileFixDialogProps> = ({
    deliveryFileName,
    filePath,
    cloudFileId,
    cloudAssetId,
    isOpen,
    onClose
}: RequestCloudFileFixDialogProps) => {
    const { requestCloudFileFix } = useRequestCloudFileFix({ invalidationKey: cloudFileId });
    const { validate } = useRequestCloudFixValidation();

    const { success, error } = useNotifications();

    const [validationResult, setValidationResult] = useState<ValidationResult | undefined>();
    const [fileName, setFileName] = useState<string>(deliveryFileName);

    useEffect(() => {
        setFileName(deliveryFileName);
    }, [deliveryFileName]);

    const handleConfirm = async () => {
        const result = await validateAndSetResult();

        if (!result?.isValid) {
            return;
        }

        try {
            await requestCloudFileFix.mutateAsync({ cloudFileId, cloudAssetId, filename: fileName });

            success("Cloud fix requested successfully");
        } catch (e) {
            error("Something went wrong while sending to cloud fix");
        } finally {
            handleClose();
        }
    };

    const validateAndSetResult = async () => {
        const result = await validate(fileName, filePath);
        setValidationResult(result);

        return result;
    };

    const handleClose = () => {
        setValidationResult(undefined);
        onClose();
    };

    return (
        <ConfirmDialog
            width={"40%"}
            title="Request cloud fix"
            text="Please specify the destination path where the file should be copied. Please note that operation may take a while for larger files."
            hidden={!isOpen}
            confirm={{ onClick: handleConfirm }}
            cancel={{ onClick: handleClose }}
        >
            <TextField
                defaultValue={deliveryFileName}
                label={"Destination path"}
                onChange={(e, value) => {
                    setFileName(value ?? "");
                }}
            />
            <FormValidationSummary validationResult={validationResult} />
        </ConfirmDialog>
    );
};
